import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { Reveal, Overline } from "@/components/motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SERVICE_OPTIONS, WHATSAPP_URL } from "@/data/site";

const empty = { name: "", phone: "", address: "", service: "", service_type: "pickup_delivery", notes: "" };

const Booking = () => {
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.service) {
      toast.error("Please fill in name, phone, address and service.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", { ...form, pickup_date: date ? format(date, "yyyy-MM-dd") : null });
      setDone(true);
      toast.success("Pickup requested! We'll call you shortly to confirm.");
      setForm(empty); setDate(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try calling us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="py-24 md:py-36 max-w-[1600px] mx-auto px-5 md:px-10" data-testid="booking-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <Reveal><Overline>Book a Pickup</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95]">
              Doorstep pickup.<br />Zero effort.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base font-light text-foreground/70 leading-relaxed max-w-md">
              Fill the slip and we&apos;ll arrive at your door across South Delhi. Free pickup &amp; delivery on orders above ₹500.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-whatsapp"
                className="group relative inline-flex items-center gap-2.5 border border-foreground px-6 py-3.5 text-sm font-medium uppercase tracking-widest overflow-hidden"
              >
                <MessageCircle size={16} className="relative z-10 transition-colors duration-300 group-hover:text-background" />
                <span className="relative z-10 transition-colors duration-300 group-hover:text-background">Book on WhatsApp</span>
                <span className="absolute inset-0 bg-foreground translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              </a>
              <p className="mt-3 text-xs font-light text-foreground/60">Prefer to chat? Message us directly — we reply fast.</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            {done ? (
              <div className="border border-foreground/15 p-10 md:p-14 flex flex-col items-start gap-5" data-testid="booking-success">
                <CheckCircle2 size={44} className="text-foreground" />
                <h3 className="font-serif text-3xl md:text-4xl font-light">Request received.</h3>
                <p className="text-foreground/70 font-light max-w-md">
                  Thank you. Our team will call you shortly to confirm your pickup time. Keep your garments ready!</p>
                <div className="flex flex-wrap gap-3 mt-2">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="booking-success-whatsapp"
                    className="inline-flex items-center gap-2 bg-foreground text-background px-6 py-3 text-xs uppercase tracking-widest hover:bg-foreground/85 transition-colors"
                  >
                    <MessageCircle size={15} /> Chat on WhatsApp
                  </a>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="booking-another"
                    className="border border-foreground px-6 py-3 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors"
                  >
                    Book Another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-foreground/15 p-8 md:p-12" data-testid="booking-form">
                <div className="flex items-baseline justify-between border-b border-foreground/20 pb-4 mb-8">
                  <span className="font-serif text-2xl">Pickup Slip</span>
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground/50">Stainless · SD</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                  <label className="block">
                    <span className="text-xs uppercase tracking-widest text-foreground/50">Full Name</span>
                    <input data-testid="booking-name" className="slip-input mt-2" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-widest text-foreground/50">Phone</span>
                    <input data-testid="booking-phone" className="slip-input mt-2" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Mobile number" />
                  </label>

                  <label className="block md:col-span-2">
                    <span className="text-xs uppercase tracking-widest text-foreground/50">Pickup Address</span>
                    <input data-testid="booking-address" className="slip-input mt-2" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="House / street / locality, South Delhi" />
                  </label>

                  <div className="block">
                    <span className="text-xs uppercase tracking-widest text-foreground/50">Service</span>
                    <Select value={form.service} onValueChange={(v) => set("service", v)}>
                      <SelectTrigger data-testid="booking-service" className="mt-2 rounded-none border-0 border-b border-foreground/30 bg-transparent px-0 focus:ring-0 focus:border-foreground">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="block">
                    <span className="text-xs uppercase tracking-widest text-foreground/50">Preferred Pickup Date</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" data-testid="booking-date" className="slip-input mt-2 flex items-center justify-between text-left">
                          <span className={date ? "" : "text-foreground/40"}>{date ? format(date, "PPP") : "Pick a date"}</span>
                          <CalendarIcon size={16} className="text-foreground/50" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-none" align="start">
                        <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <label className="block md:col-span-2">
                    <span className="text-xs uppercase tracking-widest text-foreground/50">Notes (optional)</span>
                    <input data-testid="booking-notes" className="slip-input mt-2" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Special instructions, stains, fabric details…" />
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="group relative mt-10 w-full bg-foreground text-background py-4 text-sm font-medium uppercase tracking-widest overflow-hidden disabled:opacity-60"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2 transition-colors duration-300 group-hover:text-foreground">
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? "Submitting…" : "Request Pickup"}
                  </span>
                  <span className="absolute inset-0 bg-background translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Booking;
